import { create } from 'zustand';
import type { Bounds, Camera, Vec2, Viewport } from '@/types';
import { DEFAULT_CAMERA } from '@/constants/camera';
import { clampZoom, panCameraByScreenDelta, zoomCameraAt } from '@/core/coordinates/coordinateSystem';
import {
  camerasEqual,
  centerCameraOn,
  centerCameraOnBounds,
  fitCameraToBounds,
  setCameraZoom,
  zoomCameraBy,
  type FitOptions,
} from '@/core/camera/cameraOps';
import { applyPinch, type PinchPoints } from '@/core/interaction/pinchGesture';

/**
 * High-frequency interaction state. The camera changes on every pointer move
 * while panning, so it lives apart from document state and is consumed through
 * `subscribe` + direct DOM writes rather than React re-renders (see docs/performance.md).
 *
 * Every mutation goes through a pure function in `core/camera` or `core/coordinates`;
 * the store only holds the result and drops no-op updates.
 */
export interface CameraStore {
  readonly camera: Camera;
  readonly viewport: Viewport;
  readonly setCamera: (camera: Camera) => void;
  readonly panByScreenDelta: (delta: Vec2) => void;
  readonly zoomAt: (nextZoom: number, screenAnchor: Vec2) => void;
  /** Multiplies zoom about the viewport centre. */
  readonly zoomBy: (factor: number) => void;
  /** Sets zoom about the viewport centre (e.g. reset to 100%). */
  readonly setZoom: (zoom: number) => void;
  readonly pinch: (prev: PinchPoints, next: PinchPoints) => void;
  readonly centerOn: (world: Vec2, zoom?: number) => void;
  readonly centerOnBounds: (bounds: Bounds) => void;
  readonly fitToBounds: (bounds: Bounds, options?: FitOptions) => void;
  readonly setViewport: (viewport: Viewport) => void;
}

export const useCameraStore = create<CameraStore>()((set, get) => {
  const update = (next: Camera) => {
    if (!camerasEqual(get().camera, next)) set({ camera: next });
  };
  return {
    camera: DEFAULT_CAMERA,
    viewport: { size: { width: 0, height: 0 }, devicePixelRatio: 1 },
    setCamera: (camera) => {
      update({ ...camera, zoom: clampZoom(camera.zoom) });
    },
    panByScreenDelta: (delta) => {
      if (delta.x === 0 && delta.y === 0) return;
      update(panCameraByScreenDelta(get().camera, delta));
    },
    zoomAt: (nextZoom, anchor) => {
      update(zoomCameraAt(get().camera, nextZoom, anchor));
    },
    zoomBy: (factor) => {
      const { camera, viewport } = get();
      update(zoomCameraBy(camera, factor, viewport.size));
    },
    setZoom: (zoom) => {
      const { camera, viewport } = get();
      update(setCameraZoom(camera, zoom, viewport.size));
    },
    pinch: (prev, next) => {
      update(applyPinch(get().camera, prev, next));
    },
    centerOn: (world, zoom) => {
      const { camera, viewport } = get();
      update(centerCameraOn(world, viewport.size, zoom ?? camera.zoom));
    },
    centerOnBounds: (bounds) => {
      const { camera, viewport } = get();
      update(centerCameraOnBounds(camera, bounds, viewport.size));
    },
    fitToBounds: (bounds, options) => {
      update(fitCameraToBounds(bounds, get().viewport.size, options));
    },
    setViewport: (viewport) => {
      const v = get().viewport;
      if (
        v.size.width === viewport.size.width &&
        v.size.height === viewport.size.height &&
        v.devicePixelRatio === viewport.devicePixelRatio
      )
        return;
      set({ viewport });
    },
  };
});
